import React from 'react';

interface DisplayStarRatingProps {
  rating: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  sm: 'text-sm',
  md: 'text-xl',
  lg: 'text-2xl',
};

/**
 * Snap a rating to display stars (half-star bands).
 * ≥4.75→5, ≥4.25→4.5, … ≥0.75→1, else 0.
 */
export function bandedDisplayStars(rating: number): number {
  const r = Math.min(5, Math.max(0, Number(rating) || 0));
  if (!Number.isFinite(r)) return 0;
  if (r >= 4.75) return 5;
  if (r >= 4.25) return 4.5;
  if (r >= 3.75) return 4;
  if (r >= 3.25) return 3.5;
  if (r >= 2.75) return 3;
  if (r >= 2.25) return 2.5;
  if (r >= 1.75) return 2;
  if (r >= 1.25) return 1.5;
  if (r >= 0.75) return 1;
  return 0;
}

/** Fill for star index 0–4 given banded display value: 0 | 0.5 | 1 */
function starFill(banded: number, index: number): number {
  const rem = banded - index;
  if (rem >= 1) return 1;
  if (rem >= 0.5) return 0.5;
  return 0;
}

/** Read-only stars using BuyDrop half-star banding (same on web / iOS / Android). */
const DisplayStarRating: React.FC<DisplayStarRatingProps> = ({
  rating,
  size = 'md',
  className = '',
}) => {
  const raw = Math.min(5, Math.max(0, Number(rating) || 0));
  const banded = bandedDisplayStars(raw);

  return (
    <div
      className={`flex items-center ${className}`}
      aria-label={`${raw.toFixed(1)} out of 5 stars`}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const fill = starFill(banded, index);
        return (
          <span
            key={index}
            className={`relative inline-block leading-none ${sizeClasses[size]}`}
            style={{ width: '1em' }}
          >
            <span className="text-gray-300" aria-hidden>
              ★
            </span>
            {fill > 0 && (
              <span
                className="absolute left-0 top-0 overflow-hidden text-yellow-400 whitespace-nowrap"
                style={{ width: `${fill * 100}%` }}
                aria-hidden
              >
                ★
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

export default DisplayStarRating;
